import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import { Loader2, ImageIcon, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  useDagMetadata,
  useDagRows,
  useDropdownLookups,
} from '@/hooks/useDagTable'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import { buildDagColumnDefs } from '@/components/PlanDetail/buildDagColumnDefs'
import {
  DagColumnHeaderLabel,
  type DagColumnHeaderLabelProps,
} from '@/components/PlanDetail/DagColumnHeaderLabel'
import { DagCellRenderer } from '@/components/PlanDetail/DagCellRenderer'
import { ColumnSelectorPopover } from '@/components/PlanDetail/ColumnSelectorPopover'
import { getConfigurableColumns } from '@/components/PlanDetail/columnConfiguration'
import { LineItemFilterButton } from '@/components/PlanDetail/LineItemFilterButton'
import { isFilterableColumn } from '@/components/PlanDetail/dagFilterUtils'
import {
  dagRowToRowData,
  type RowData,
} from '@/components/PlanDetail/useProductsTableState'
import type { FilterGroup } from '@/types'

ModuleRegistry.registerModules([AllCommunityModule])

// ---------------------------------------------------------------------------
// Read-only cell renderers
// ---------------------------------------------------------------------------

function ReadOnlyNameHeader(props: DagColumnHeaderLabelProps) {
  return (
    <div className="flex items-center gap-2.5 pl-4">
      <DagColumnHeaderLabel {...props} />
    </div>
  )
}

function ReadOnlyNameCell({ data }: { data?: RowData }) {
  if (!data || data.isOptionRow) return null

  const description = String(data.cells?.['description'] ?? '')
  const thumbnailUrl = data.images?.default_thumbnail_url ?? null

  return (
    <div className="flex items-center gap-2.5 w-full h-full px-4">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt="Item thumbnail"
          loading="lazy"
          className="w-[43px] h-[48px] object-cover rounded shrink-0"
        />
      ) : (
        <div className="w-[43px] h-[48px] bg-[#f8f8f8] rounded flex items-center justify-center shrink-0">
          <ImageIcon className="size-6 text-muted-foreground" />
        </div>
      )}
      <div className="flex flex-col justify-center min-w-0 flex-1">
        <span className="text-sm font-medium truncate">
          {description || '-'}
        </span>
      </div>
    </div>
  )
}

function ReadOnlySkuCell({ data }: { data?: RowData }) {
  if (!data || data.isOptionRow) return null
  const sku = data.cells?.['plan_sku_num']
  return (
    <div className="flex items-center min-w-0 w-full h-full px-2">
      <span className="text-sm truncate">
        {sku != null ? String(sku) : '-'}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ReadOnlyProductsTableProps {
  planId: string
  versionId: string
  primaryAttributes?: string[]
}

export function ReadOnlyProductsTable({
  planId,
  versionId,
  primaryAttributes = [],
}: ReadOnlyProductsTableProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.APPROVAL_LINE_ITEMS,
    20
  )
  const [searchQuery, setSearchQuery] = useState('')
  const prevSearchRef = useRef(searchQuery)

  useEffect(() => {
    if (prevSearchRef.current !== searchQuery) {
      prevSearchRef.current = searchQuery
      setPage(1)
    }
  }, [searchQuery])

  // Debounced search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        setSearchQuery(value)
      }, 300)
    },
    []
  )
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // Advanced filter state (AND/OR rule tree)
  const [filterGroup, setFilterGroup] = useState<FilterGroup | null>(null)
  const handleApplyFilter = useCallback((next: FilterGroup | null) => {
    setFilterGroup(next)
    setPage(1)
  }, [])

  // Data fetching
  const { data: metadata, isLoading: metaLoading } = useDagMetadata(
    planId,
    versionId
  )
  const {
    data: rowsData,
    isLoading: rowsLoading,
    isFetching: rowsFetching,
  } = useDagRows(planId, versionId, page, pageSize, searchQuery, filterGroup)
  const { data: dropdownData } = useDropdownLookups()

  const isLoading = metaLoading || rowsLoading
  const rows = useMemo(() => rowsData?.rows ?? [], [rowsData])
  const totalPages = rowsData?.pagination
    ? Math.ceil(rowsData.pagination.total / pageSize)
    : 1

  const dropdownMap = useMemo(
    () => dropdownData?.categories ?? {},
    [dropdownData]
  )

  // Column visibility
  const effectiveHiddenColumnIds = useColumnVisibility(
    planId,
    versionId,
    primaryAttributes,
    metadata?.columns
  )

  const selectableColumns = useMemo(
    () => (metadata?.columns ? getConfigurableColumns(metadata.columns) : []),
    [metadata]
  )
  const selectableGroups = useMemo(
    () =>
      metadata?.groups.filter(
        g => g !== 'Identity' && selectableColumns.some(c => c.group === g)
      ) ?? [],
    [metadata, selectableColumns]
  )

  const filterableColumns = useMemo(
    () => metadata?.columns.filter(isFilterableColumn) ?? [],
    [metadata]
  )

  // Convert rows to RowData
  const rowData: RowData[] = useMemo(() => rows.map(dagRowToRowData), [rows])

  // Column definitions
  const columnDefs = useMemo(() => {
    if (!metadata) return []
    return buildDagColumnDefs({
      columns: metadata.columns,
      groups: metadata.groups,
      dropdownMap,
      hiddenColumnIds: effectiveHiddenColumnIds,
      isReadOnly: true,
      showActions: false,
      NameHeaderComponent: ReadOnlyNameHeader,
      NameCellRenderer: ReadOnlyNameCell,
      SkuCellRenderer: ReadOnlySkuCell,
      ActionsCellRenderer: () => null,
      DagCellRendererComponent: DagCellRenderer,
    })
  }, [metadata, dropdownMap, effectiveHiddenColumnIds])

  // Grid context for DagCellRenderer
  const gridContext = useMemo(
    () => ({
      isReadOnly: true,
      dropdownMap,
      planId,
      versionId,
    }),
    [dropdownMap, planId, versionId]
  )

  if (isLoading && !rows.length) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Toolbar: search + column picker */}
      <div className="flex items-center justify-end gap-3 mb-4">
        <div className="relative w-full max-w-[352px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            onChange={handleSearchChange}
            className="pl-9 bg-muted/50"
          />
        </div>
        {filterableColumns.length > 0 && (
          <LineItemFilterButton
            columns={filterableColumns}
            dropdownMap={dropdownMap}
            filter={filterGroup}
            onApplyFilter={handleApplyFilter}
            isApplying={rowsFetching}
          />
        )}
        {selectableColumns.length > 0 && selectableGroups.length > 0 && (
          <ColumnSelectorPopover
            columns={selectableColumns}
            groups={selectableGroups}
          />
        )}
      </div>

      <div className="border border-[#e7e7e7] bg-white overflow-hidden rounded-md">
        <div className="ag-theme-michaels approval-detail-grid w-full">
          <AgGridReact<RowData>
            rowData={rowData}
            columnDefs={columnDefs}
            theme="legacy"
            loading={rowsLoading}
            domLayout="autoHeight"
            rowHeight={68}
            headerHeight={49}
            groupHeaderHeight={49}
            overlayNoRowsTemplate="<span>No products found</span>"
            suppressCellFocus={true}
            suppressRowHoverHighlight={false}
            suppressColumnVirtualisation={true}
            suppressMovableColumns={true}
            context={gridContext}
            getRowId={params => params.data.id}
          />
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[20, 50, 100, 200]}
        />
      </div>
    </div>
  )
}
